<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'unemployedNewAndActiveList' }"
          marginBottom="5"/>

      <h6 class="element-header">Kundendetails</h6>

      <div class="element-box">
        <UnemployedDetails :unemployed="unemployed"/>
        <CoachingMenu :unemployed="unemployed" :coachingCounts="coachingCounts"/>

        <div class="row mb-2 mt-4" style="padding: 0 10px">
          <div class="col-lg-6">
            <h6>Gruppen Maßnahmen</h6>
          </div>
          <div class="col-lg-6 text-end">
            <button type="button" class="btn btn-primary mb-2 me-2"
                    style="padding: 5px 7px;"
                    @click="handleModalForm">
              Neue Maßnahme anlegen
            </button>
          </div>
        </div>

        <div class="table-responsive overflow-auto mt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Status</th>
              <th>Maßnahmenart</th>
              <th>Standort</th>
              <th>Start</th>
              <th>Ende</th>
              <th>Stand</th>
              <th>Stand %</th>
              <th>UE fehlt</th>
              <th>Coach</th>
              <th>Geva Testung</th>
              <th>Noch abzurechnende Stunden</th>
              <th>Letzter Termin</th>
              <th>Bericht</th>
              <th>Bearbeiten</th>
              <th v-if="isAdmin">Historie</th>
              <th>Löschen</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(coaching, index) in groupCoachingList" :key="index">
              <td>
                <StatusBadge :status="coaching.status" :label="coaching.statusTranslated"/>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.nameOfCoaching.operationTypeTranslated }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.unemployed?.location?.name }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.startDate }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.endDate }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.unitsDone }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.unitsDonePercent }} %
                </strong>
              </td>
              <td style="text-align: center">
                <strong class="js-lists-values-employee-name">
                  {{ coaching.notSignedUnits }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  <template v-for="(coach, index) in coaching.coaches" :key="index">
                    {{ coach.firstName }} {{ coach.lastName }}<br/>
                  </template>
                </strong>

              </td>
              <td style="text-align: center">
                <strong class="js-lists-values-employee-name">
                  {{ coaching.gevaStatusTranslated }}
                </strong>
              </td>
              <td style="text-align: center">
                <strong class="js-lists-values-employee-name">
                  {{ coaching.countNotBilledMeetings }}
                </strong>
              </td>
              <td>
                <strong class="js-lists-values-employee-name">
                  {{ coaching.lastUnit }}
                </strong>
              </td>
              <td>
                <ReportButton
                    :to="{ name: 'groupCoachingFirstInterviewForm', params: { id: coaching.id } }"/>
              </td>
              <td>
                <EditButton @click="handleEditModalForm(coaching.id)"/>
              </td>

              <!-- Change history PDF (admin only) -->
              <td v-if="isAdmin">
                <HistoryButton
                    @click="downloadFile(`/unemployed/group-coaching/history-pdf/${coaching.id}`, undefined)"/>
              </td>
              <!-- Delete -->
              <td class="text-center">
                <DeleteButton @click="handleDeleteModal(coaching?.id)"/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Modal
          v-model="showModalForm"
          title="Gruppen Maßnahmen"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-lg"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form
            :groupCoachingId="groupCoachingId"
            :unemployedId="unemployed?.id"
            :locationId="unemployed?.location?.id"
            :locationName="unemployed?.location?.name"
            v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteGroupCoaching">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup>
import unemployedService from '@/api/Unemployed';
import groupCoachingService from '@/api/Unemployed/GroupCoaching';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import CoachingMenu from '@/views/App/Unemployed/Coaching/CoachingMenu.vue';
import Form from '@/views/App/Unemployed/Coaching/GroupCoaching/Form.vue';
import UnemployedDetails from '@/views/App/Unemployed/UnemployedDetails.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import ReportButton from "@/components/button/ReportButton.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import EditButton from "@/components/button/EditButton.vue";
import DeleteButton from "@/components/button/DeleteButton.vue";
import HistoryButton from "@/components/button/HistoryButton.vue";
import {piniaUserStore} from "@/store/piniaUserStore";
import {UserRole} from "@/shared/enums/UserRolesEnums";
import {downloadFile} from "@/shared/functions";

const piniaUserState = piniaUserStore();
const isAdmin        = computed(() => piniaUserState.currentUser?.user?.roles?.[0] === UserRole.ADMIN);

const { t }             = useI18n();
const route             = useRoute();
const unemployedId      = route.params.id;
const unemployed        = ref(null);
const coachingCounts    = ref(null);
const groupCoachingList = ref([]);
const groupCoachingId   = ref(null);
const isLoaded          = ref(false);
const submitForm        = ref(false);
const showModalForm     = ref(false);
const showDeleteModal   = ref(false);

async function getUnemployedOverviewData() {
  try {
    const { data }       = await unemployedService.getUnemployedOverview(unemployedId);
    unemployed.value     = data.data.unemployed;
    coachingCounts.value = data.data.coachingCounts;

  } catch (error) {
    await ErrorToast(error);
  }
}

async function getGroupCoachingList() {
  try {
    const { data } = await groupCoachingService.getGroupCoachingList(
        unemployedId,
    );

    groupCoachingList.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

watch(submitForm, () => {
  groupCoachingId.value = null;
  showModalForm.value   = false;
  showDeleteModal.value = false;
  getUnemployedOverviewData();
  getGroupCoachingList();
});

function handleModalForm() {
  CloseToast();
  groupCoachingId.value = null; // Important
  showModalForm.value   = true;
  showDeleteModal.value = false;
}

function handleDeleteModal(id) {
  CloseToast();
  groupCoachingId.value = id;
  showDeleteModal.value = true;
  showModalForm.value   = false;
}

function handleEditModalForm(id) {
  CloseToast();
  groupCoachingId.value = id;
  showModalForm.value   = true;
  showDeleteModal.value = false;
}

async function deleteGroupCoaching() {
  try {
    await groupCoachingService.deleteGroupCoaching(groupCoachingId.value);

    groupCoachingId.value = null;
    showDeleteModal.value = false;

    await getUnemployedOverviewData();
    await getGroupCoachingList();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  CloseToast();

  await Promise.all([
    getUnemployedOverviewData(),
    getGroupCoachingList(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>